// First-party analytics. Fire-and-forget POST to /track (anonymous) or
// /track/me (authed, with the Bearer token so the server can attribute the
// event to a verified userId from the JWT — never trusts a userId in the
// body). All calls swallow errors: analytics must never break the app.

import { config } from './config';
import { getIdToken } from './auth';

const SESSION_KEY = 'sp.sessionId';

// Per-tab session: closing the tab ends the journey, which gives the cleanest
// journey-replay data for the admin dash. If we ever want cross-session
// retention, we'd add a separate longer-lived id alongside this one.
function getSessionId(): string {
  if (typeof window === 'undefined') return '';
  try {
    const existing = window.sessionStorage.getItem(SESSION_KEY);
    if (existing) return existing;
    const id: string = (crypto as any).randomUUID
      ? (crypto as any).randomUUID()
      : `s_${Date.now().toString(36)}_${Math.random().toString(36).slice(2, 10)}`;
    window.sessionStorage.setItem(SESSION_KEY, id);
    return id;
  } catch {
    return '';
  }
}

export function track(event: string, meta?: Record<string, unknown>) {
  if (typeof window === 'undefined' || !event) return;
  const sessionId = getSessionId();
  if (!sessionId) return;

  const idToken = getIdToken();
  const path = idToken ? '/track/me' : '/track';

  const body = JSON.stringify({
    event,
    sessionId,
    path: window.location.pathname || null,
    meta: meta ?? null,
  });

  // keepalive=true so the request survives if the user navigates away
  // immediately after we fire it (e.g. tracking a click that triggers a redirect).
  fetch(`${config.apiBase}${path}`, {
    method: 'POST',
    headers: {
      'content-type': 'application/json',
      ...(idToken ? { authorization: `Bearer ${idToken}` } : {}),
    },
    body,
    keepalive: true,
  }).catch(() => { /* analytics is fire-and-forget */ });
}
